import './PauseCreativeBuilder.scss';

import { useFormikContext } from 'formik';
import React from 'react';

import type { PauseCreativeInputV5 } from '../../../../apis/graphql';
import { removeTypeNamesFromObject } from '../../../../apis/graphql';
import { ColorPicker, TextField } from '../../../../common/Form';
import AssetImporterField from '../../../../common/Form/AssetImporterField';
import { assetTypes } from '../../../../configs';
import bem from '../../../../utils/bem';
import type { AdFormValues } from '../../adFormik';
import { AdFormCreativeFields } from '../../adFormik/fields';
import { isCreativeAssetRemoveForbidden } from '../../utils/creative';
import { CreativeFooter } from '../components';
import CreativeBuilder from '../CreativeBuilder';
import type { CreativeBuilderProps } from '../CreativeBuilder/CreativeBuilder';
import { useCreativeData } from '../hooks/useCreativeData';
import type { CreativesBuilderValues } from '../types';
import { CreativeEditMode } from '../types';

const [, element] = bem('pause-creative');

const PAUSE_ASPECT_RATIO = 16 / 9;

export const constructPauseCreativeInput = ({
  name,
  image,
  altText,
  accent,
  creativeLibraryId,
}: CreativesBuilderValues): PauseCreativeInputV5 => {
  const input: PauseCreativeInputV5 = {
    name,
    type: 'PAUSE',
    altText: altText!,
    accent: removeTypeNamesFromObject(accent!),
  };

  if (creativeLibraryId) input.creativeLibraryId = creativeLibraryId;

  if (image?.id !== '') input.imageId = image?.id;

  return input;
};

function PauseCreativeBuilder({ isPreview, adTypeDimensions, editMode }: CreativeBuilderProps): React.JSX.Element {
  const {
    values: { creative, lineItem },
  } = useFormikContext<AdFormValues>();

  const {
    data: { assets },
    loading,
  } = useCreativeData(creative, lineItem?.displayPublisherTarget);

  const assetReadonly = isCreativeAssetRemoveForbidden(editMode, creative);

  return (
    <CreativeBuilder
      lineItemPublishers={lineItem?.displayPublisherTarget || []}
      lineItemCountry={lineItem?.country}
      classNameModifier="pause"
      isPreview={isPreview}
      editMode={editMode}
    >
      <div className={element('grid')}>
        <div className={element('left-column')}>
          <AssetImporterField
            adTypeDimensions={adTypeDimensions?.display}
            aspectRatio={PAUSE_ASPECT_RATIO}
            assetType={assetTypes.DisplayAsset.key}
            formFieldName={AdFormCreativeFields.Image}
            id="pause-image-asset"
            isPreview={isPreview}
            label="Display Image Asset"
            secondaryLabel={isPreview ? undefined : ' (Required)'}
            readonly={assetReadonly}
            lineItemCountry={lineItem?.country}
            lineItemPublisherList={lineItem?.displayPublisherTarget || []}
          />
        </div>
        <div className={element('right-column')}>
          <TextField
            id="image-alt-text"
            formFieldName={AdFormCreativeFields.AltText}
            label="Image Alt Text"
            secondaryLabel={isPreview ? undefined : ' (Required)'}
            disabled={editMode === CreativeEditMode.readonly}
            required
          />
          <ColorPicker
            id="accent-color"
            formFieldName={AdFormCreativeFields.Accent}
            label="Background Accent Color"
            secondaryLabel={isPreview ? undefined : ' (Required)'}
            placeholder="eg. 45,189,157"
            disabled={editMode === CreativeEditMode.readonly}
            required
          />
        </div>
      </div>
      <div className={element('footer')}>
        <CreativeFooter
          assets={assets}
          creative={creative}
          isLoading={loading}
          hasHrLine={false}
          lineItemData={lineItem}
        />
      </div>
    </CreativeBuilder>
  );
}

export default PauseCreativeBuilder;
